import type { BillingState, BillingStatus } from '../types'

export type StatusTone = 'success' | 'warning' | 'danger' | 'neutral'

/**
 * What a status means to the person reading it, rather than what Stripe calls
 * it.
 *
 * Stripe's vocabulary is precise and unhelpful: "past_due" does not tell an
 * office manager that nothing has broken yet and someone needs to update a
 * card. Each line here says what is true now and what happens next, because a
 * banner that only names a state leaves the reader to guess the consequence.
 */
export function describeStatus(state: BillingState): {
  tone: StatusTone
  label: string
  detail: string
} {
  const { status } = state
  const ends = formatDay(state.currentPeriodEndsAt)
  const trialEnds = formatDay(state.trialEndsAt)

  if (state.billingExempt) {
    return {
      tone: 'neutral',
      label: 'Not billed',
      detail: 'This workspace is not billed through KAAMCO. Nothing here needs attention.',
    }
  }

  const ownTrialEnds = formatDay(state.trial.endsAt)
  const days = state.trial.daysLeft
  const carryOver = state.checkoutTrial
    ? 'The days you have left carry into the subscription, so the first payment is taken when the trial ends.'
    : 'Less than two days are left, so a subscription started now is charged straight away.'

  if (!state.hasSubscription && state.access === 'trialing') {
    // Their own trial, or a cancelled subscription inside it.
    return {
      tone: days !== null && days <= 7 ? 'warning' : 'success',
      label: 'Free trial',
      detail:
        `${days === 1 ? '1 day' : `${days ?? 0} days`} left${ownTrialEnds ? `, ending ${ownTrialEnds}` : ''}. ` +
        `Choose a plan to keep the workspace writable after that. ${carryOver}`,
    }
  }

  if (!state.hasSubscription && !status) {
    return {
      tone: 'danger',
      label: 'Read-only',
      detail:
        'The free trial has ended, so this workspace is read-only, including clocking in. ' +
        'Nothing has been deleted. Choose a plan to make changes again.',
    }
  }

  if (state.cancelAtPeriodEnd && status !== 'canceled') {
    return {
      tone: 'warning',
      label: 'Ending soon',
      detail: ends
        ? `Cancelled, but paid until ${ends}. Everything keeps working until then.`
        : 'Cancelled. Everything keeps working until the period ends.',
    }
  }

  const map: Record<BillingStatus, { tone: StatusTone; label: string; detail: string }> = {
    trialing: {
      tone: 'success',
      label: 'Free trial',
      detail: trialEnds
        ? `No charge until ${trialEnds}. Your card is on file and the first payment is taken then.`
        : 'No charge yet. Your card is on file for when the trial ends.',
    },
    active: {
      tone: 'success',
      label: 'Active',
      detail: ends ? `Renews on ${ends}.` : 'Your subscription is active.',
    },
    past_due: {
      tone: 'warning',
      label: 'Payment failed',
      detail:
        'Nothing has stopped working. We will retry the card over the next few weeks — ' +
        'update it to avoid the workspace becoming read-only.',
    },
    unpaid: {
      tone: 'danger',
      label: 'Read-only',
      detail:
        'The payment could not be taken after several attempts, so this workspace is ' +
        'read-only. Pay the open invoice in Manage in Stripe and it returns at once.',
    },
    canceled: {
      tone: 'danger',
      label: 'Read-only',
      detail:
        'The subscription has ended, so this workspace is read-only. Nothing has been ' +
        'deleted — start a plan again to make changes.',
    },
    incomplete: {
      tone: 'warning',
      label: 'Finishing payment',
      detail: 'The first payment has not completed yet. Finish checkout to start the trial.',
    },
    incomplete_expired: {
      tone: 'danger',
      label: 'Checkout expired',
      detail: 'The payment was never completed. Choose a plan to start again.',
    },
    paused: {
      tone: 'warning',
      label: 'Paused',
      detail: 'Billing is paused. Contact support to resume.',
    },
  }

  return (
    (status && map[status]) ?? {
      tone: 'neutral',
      label: 'Unknown',
      detail: 'We could not read the subscription state. Contact support if this persists.',
    }
  )
}

export const formatPrice = (cents: number | null): string =>
  cents === null ? 'Contact us' : `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`

function formatDay(iso: string | null): string | undefined {
  if (!iso) return undefined
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'long' }).format(new Date(iso))
}
